import React, { useEffect, useRef, useState } from 'react'
import { View, StyleSheet, Animated, Easing } from 'react-native'
import { Text, useTheme } from '@ui-kitten/components'
import { useColors } from '../utils/useColors'
import { useTranslation } from 'react-i18next'
import Svg, { Circle } from 'react-native-svg'
const AnimatedCircle = Animated.createAnimatedComponent(Circle)

export default function Loader({ duration = 10000, action }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const colors = useColors()
  const progressAnimation = useRef(new Animated.Value(0)).current
  const [displayPercentage, setDisplayPercentage] = useState('0%')
  const hasRunRef = useRef(false)
  const size = 120
  const strokeWidth = 8
  const center = size / 2
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    progressAnimation.addListener(({ value }) => {
      const percentage = Math.round(value * 100)
      setDisplayPercentage(`${percentage}%`)
    })

    const animation = Animated.timing(progressAnimation, {
      toValue: 1,
      duration: duration,
      easing: Easing.linear,
      useNativeDriver: false,
    })

    animation.start()

    const timer = setTimeout(() => {
      action()
    }, duration)

    return () => {
      animation.stop()
      progressAnimation.removeAllListeners()
      clearTimeout(timer)
    }
  }, [])

  const strokeDashoffset = progressAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [circumference, 0],
  })

  return (
    <View style={styles.container}>
      <View style={styles.circleContainer}>
        <Svg width={size} height={size}>
          {/* Background circle */}
          <Circle
            stroke={theme['border-basic-color-3']}
            fill="none"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <AnimatedCircle
            stroke={colors.primary}
            fill="none"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </Svg>
        <Text style={[styles.percentageText, { color: theme['text-basic-color'] }]}>
          {displayPercentage}
        </Text>
      </View>
      <Text category='s2' style={[styles.bottomText, { color: theme['text-hint-color'] }]}>
        {t('funnel.creatingOrg')}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 30,
  },
  circleContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  percentageText: {
    position: 'absolute',
    fontSize: 24,
    fontWeight: 'bold',
  },
  bottomText: {
    textAlign: 'center',
  },
}) 